import { ActionError, Button, StepProgress, cn } from '@westhetics/ui'
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { PublicScreen, publicTextAction } from '@/app/public-screen'
import { noteText, ReadingSkeleton, supportText } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { onlyDigits } from '@/lib/masks'
import {
  registrationPaths,
  useEmailChangeCode,
  useEmailChangeVerify,
  useIdentityCode,
  useIdentityLookup,
  useIdentityVerify,
  type IdentityDocument,
  type IdentityOptions,
  type Prefilled,
} from '../identity/api'
import { CodePanel } from '../identity/code-panel'
import { documentKey, toIdentityDocument } from '../identity/document'
import { EmailChange } from '../identity/email-change'
import { useOpening, usePostalCode, useRegister, type Opening, type Registered } from './api'
import {
  applyPrefill,
  documentValuesOf,
  EMPTY_VALUES,
  MESSAGES,
  STEP_TITLES,
  fromApiFields,
  isMinorOn,
  stepOf,
  stepsFor,
  summaryOf,
  toRegisterInput,
  todayIso,
  validateStep,
  type FieldErrors,
  type FieldKey,
  type RegisterValues,
  type StepId,
} from './form'
import { countryOptions } from './labels'
import { ContactStep, GuardianStep, ReviewStep, YouStep, type StepProps } from './register-steps'

/**
 * Cadastro pelo link da clínica (docs/areas/portal-do-paciente.md, "Cadastro pelo link da clínica";
 * RN-POR-18; docs/areas/pacientes.md RN-PAC-31 a 35 e 42; T52). Arquétipo: fluxo em etapas, no
 * celular. Sem sessão: o token do link identifica a clínica. Pede o cadastro que a paciente sabe
 * responder em três ou quatro etapas ("Seus dados", "Contato e endereço", "Responsável legal" quando
 * há, "Conferir e enviar"); só nome, WhatsApp e o aceite da nota de privacidade são obrigatórios, e o
 * responsável é exigido de quem tem menos de 18 anos. Termina com a entrada do portal quando há
 * e-mail.
 */

const LINK_INVALID = 'Peça à clínica o link de cadastro.'

const TOKEN_FORMAT = /^[0-9a-f]{32}$/

/** O logo da clínica à direita do wordmark; sem logo, nada: o nome já está no título. */
function ClinicMark({ clinic }: { clinic: Opening['clinic'] }) {
  if (!clinic.logoUrl) return null
  return (
    <img
      src={clinic.logoUrl}
      alt={clinic.name}
      className="h-10 w-auto max-w-[140px] object-contain"
    />
  )
}

function InvalidLink({ message }: { message: string }) {
  return (
    <PublicScreen eyebrow="Cadastro" title="Este link não é válido.">
      <p className={cn(supportText, 'mt-3')}>{message}</p>
    </PublicScreen>
  )
}

function Done({ result }: { result: Registered }) {
  return (
    <PublicScreen eyebrow="Cadastro" title={`Pronto, ${result.patient.firstName}.`}>
      <p className={cn(supportText, 'mt-3')}>
        A {result.clinic.name} já tem seus dados. Você chega com tudo pronto.
      </p>
      {result.patient.email ? (
        <>
          <p className={cn(supportText, 'mt-3 break-words')}>
            Com o seu documento e o código no e-mail{' '}
            <span className="text-ink">{result.patient.email}</span> você entra no portal do
            paciente e acompanha seus atendimentos e documentos.
          </p>
          <Button size="lg" className="mt-7" asChild>
            <a href={result.portalUrl}>Entrar no portal do paciente</a>
          </Button>
        </>
      ) : (
        <p className={cn(noteText, 'mt-3')}>
          Se quiser acompanhar seus documentos pelo portal do paciente, informe um e-mail à clínica.
        </p>
      )}
    </PublicScreen>
  )
}

export function RegisterPage() {
  const [params] = useSearchParams()
  const raw = params.get('t')?.trim() ?? ''
  const token = TOKEN_FORMAT.test(raw) ? raw : null
  const opening = useOpening(token)
  const [done, setDone] = useState<Registered | null>(null)

  if (!token) return <InvalidLink message={LINK_INVALID} />
  if (done) return <Done result={done} />
  if (opening.isPending) {
    return (
      <PublicScreen eyebrow="Cadastro" title="Abrindo o cadastro…">
        <div className="mt-8">
          <ReadingSkeleton label="Carregando o cadastro" />
        </div>
      </PublicScreen>
    )
  }
  if (opening.isError) {
    const error = opening.error
    if (isApiError(error) && (error.status === 404 || error.status === 403)) {
      return <InvalidLink message={error.status === 404 ? LINK_INVALID : error.message} />
    }
    return (
      <PublicScreen eyebrow="Cadastro" title="Faça seu cadastro.">
        <LoadError
          className="mt-6"
          title="Não conseguimos abrir o cadastro"
          error={error}
          onRetry={() => void opening.refetch()}
        />
      </PublicScreen>
    )
  }
  return <RegisterFlow token={token} opening={opening.data} onDone={setDone} />
}

function RegisterFlow({
  token,
  opening,
  onDone,
}: {
  token: string
  opening: Opening
  onDone: (result: Registered) => void
}) {
  const id = useId()
  const register = useRegister(token)
  const lookup = usePostalCode(token)
  // O preenchimento rápido (RN-PAC-50 a 55, T58): o documento com e-mail na Westhetics só segue com o
  // código; confirmado, os dados chegam preenchidos e o e-mail fica o confirmado.
  const paths = registrationPaths(token)
  const identityLookup = useIdentityLookup(paths)
  const identityCode = useIdentityCode(paths)
  const identityVerify = useIdentityVerify<Prefilled>(paths)
  const emailCode = useEmailChangeCode(paths)
  const emailVerify = useEmailChangeVerify(paths)
  const [identity, setIdentity] = useState<{
    key: string
    document: IdentityDocument
    options: IdentityOptions
    prefillId: string | null
    emailGrantId: string | null
  } | null>(null)
  const [changingEmail, setChangingEmail] = useState(false)
  const countries = useMemo(() => countryOptions(), [])
  const [values, setValues] = useState<RegisterValues>(EMPTY_VALUES)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [step, setStep] = useState<StepId>('you')
  const [touched, setTouched] = useState<ReadonlySet<FieldKey>>(new Set())
  const [attempted, setAttempted] = useState<ReadonlySet<StepId>>(new Set())
  // Os erros por campo do último 422, até a pessoa mexer no campo.
  const [serverErrors, setServerErrors] = useState<FieldErrors>({})
  const headingRef = useRef<HTMLHeadingElement>(null)
  const firstRender = useRef(true)

  const today = todayIso()
  const minor = isMinorOn(values.birthDate, today)
  const steps = stepsFor(values, today)
  const index = Math.max(0, steps.indexOf(step))
  const own: FieldErrors = {
    ...validateStep(step, values, today),
    ...(step === 'review' && !privacyAccepted ? { privacyAccepted: MESSAGES.privacy } : {}),
  }

  // Etapa nova: o foco vai para o título dela, e a tela volta ao topo.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    headingRef.current?.focus()
    if (window.scrollY > 0) window.scrollTo(0, 0)
  }, [step])

  const fieldId = (key: FieldKey) => `${id}-${key}`
  const errorOf = (key: FieldKey) =>
    serverErrors[key] ?? (attempted.has(step) || touched.has(key) ? own[key] : undefined)

  function update<K extends keyof RegisterValues>(key: K, value: RegisterValues[K]) {
    setValues((current) => ({ ...current, [key]: value }))
    setServerErrors((current) => {
      if (!(key in current)) return current
      const { [key]: _gone, ...rest } = current
      return rest
    })
  }
  const touch = (key: FieldKey) => setTouched((current) => new Set(current).add(key))

  /** O CEP do Brasil com 8 dígitos novos busca o endereço; o que a consulta não tem fica como está. */
  function changePostalCode(masked: string) {
    const digits = onlyDigits(masked)
    const changed = digits !== onlyDigits(values.postalCode)
    update('postalCode', masked)
    if (!changed) return
    if (digits.length < 8) {
      lookup.reset()
      return
    }
    lookup.mutate(digits, {
      onSuccess: (address) => {
        update('city', address.city)
        update('state', address.state)
        if (address.street) update('street', address.street)
        if (address.district) update('district', address.district)
      },
    })
  }
  const postalCodeHint = lookup.isPending
    ? 'Buscando o endereço…'
    : lookup.isError
      ? errorMessage(lookup.error)
      : undefined

  /** O primeiro campo com erro da etapa recebe o foco. */
  function focusFirst(errors: FieldErrors) {
    const first = Object.keys(errors)[0] as FieldKey | undefined
    if (first) document.getElementById(fieldId(first))?.focus()
  }

  function goTo(next: StepId) {
    register.reset()
    setStep(next)
  }

  const typedDocument = toIdentityDocument(documentValuesOf(values))
  const currentKey = documentKey(typedDocument)
  // A confirmação vale para o documento confirmado; mexer nele pede de novo.
  const verified = identity && identity.key === currentKey && identity.prefillId ? identity : null
  const awaitingCode =
    step === 'you' && identity && identity.key === currentKey && !identity.prefillId
      ? identity
      : null

  function advance(event: FormEvent) {
    event.preventDefault()
    if (Object.keys(own).length > 0) {
      setAttempted((current) => new Set(current).add(step))
      focusFirst(own)
      return
    }
    if (step === 'review') {
      submit()
      return
    }
    if (step === 'you' && typedDocument && (!identity || identity.key !== currentKey)) {
      if (identityLookup.isPending) return
      const document = typedDocument
      identityLookup.mutate(document, {
        onSuccess: (options) => {
          setIdentity({ key: currentKey, document, options, prefillId: null, emailGrantId: null })
          if (options.emails.length === 0) goTo(steps[index + 1]!)
        },
      })
      return
    }
    if (awaitingCode && awaitingCode.options.emails.length > 0) return
    goTo(steps[index + 1]!)
  }

  async function confirmIdentity(emailId: string, code: string) {
    if (!awaitingCode) return
    const result = await identityVerify.mutateAsync({
      document: awaitingCode.document,
      emailId,
      code,
    })
    setValues((current) => applyPrefill(current, result.prefill))
    setIdentity({ ...awaitingCode, prefillId: result.prefillId, emailGrantId: null })
    setServerErrors({})
    goTo('contact')
  }

  function submit() {
    if (register.isPending) return
    const confirmed = verified?.prefillId
      ? {
          identity: {
            prefillId: verified.prefillId,
            ...(verified.emailGrantId ? { emailGrantId: verified.emailGrantId } : {}),
          },
        }
      : {}
    register.mutate(
      { ...toRegisterInput(values, today), ...confirmed },
      {
        onSuccess: onDone,
        onError: (error) => {
          const mapped = fromApiFields(fieldErrors(error))
          const keys = Object.keys(mapped) as FieldKey[]
          if (keys.length === 0) return
          setServerErrors(mapped)
          // O banco decide a menoridade no fuso da clínica: se ele pediu o responsável, a etapa aparece.
          if (mapped.guardianName && !steps.includes('guardian')) {
            setValues((current) => ({ ...current, hasGuardian: true }))
          }
          const order: StepId[] = ['you', 'contact', 'guardian', 'review']
          const target = order.find((s) => keys.some((key) => stepOf(key) === s)) ?? 'review'
          setStep(target)
        },
      },
    )
  }

  const props: StepProps = { values, update, errorOf, touch, fieldId }
  const apiFields = fieldErrors(register.error)
  const generalError =
    register.isError && Object.keys(apiFields).length === 0 ? errorMessage(register.error) : null
  const conflict = isApiError(register.error) && register.error.status === 409

  return (
    <PublicScreen
      eyebrow="Cadastro"
      title={`Faça seu cadastro na ${opening.clinic.name}.`}
      header={<ClinicMark clinic={opening.clinic} />}
      onSubmit={advance}
      wide
    >
      {step === 'you' ? (
        <p className={cn(supportText, 'mt-3')}>
          Leva poucos minutos. Só nome e WhatsApp são obrigatórios; o resto adianta o seu
          atendimento e o contrato.
        </p>
      ) : null}

      <StepProgress className="mt-7" current={index + 1} total={steps.length} />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="font-display text-ink mt-5 text-[19px] leading-[1.35] font-normal outline-none"
      >
        {STEP_TITLES[step]}
      </h2>

      {step === 'you' ? <YouStep {...props} minor={minor} countries={countries} /> : null}
      {awaitingCode && awaitingCode.options.emails.length > 0 ? (
        <section aria-label="Cadastro na Westhetics" className="border-hairline mt-8 border-t pt-6">
          <h3 className="font-display text-ink text-[15px] leading-[1.35] font-normal">
            Este documento já tem cadastro na Westhetics.
          </h3>
          <p className={cn(supportText, 'mt-2')}>
            Para seguir, confirme com o código no e-mail: seus dados chegam preenchidos, e você
            confere cada etapa antes de enviar.
          </p>
          <CodePanel
            className="mt-4"
            options={awaitingCode.options.emails}
            onSend={(emailId) =>
              identityCode.mutateAsync({ document: awaitingCode.document, emailId })
            }
            onConfirm={confirmIdentity}
            confirmLabel="Usar meus dados"
            confirmingLabel="Confirmando…"
          />
          <p className={cn(noteText, 'mt-5')}>
            Se não tem mais acesso a esse e-mail, faça o cadastro com a recepção da clínica.
          </p>
        </section>
      ) : null}
      {identityLookup.isError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível conferir o documento"
          description={errorMessage(identityLookup.error)}
        />
      ) : null}
      {step === 'contact' ? (
        <ContactStep
          {...props}
          countries={countries}
          postalCodeHint={postalCodeHint}
          onPostalCode={changePostalCode}
          emailLocked={Boolean(verified)}
          emailChange={
            verified ? (
              changingEmail ? (
                <EmailChange
                  onSend={(email) =>
                    emailCode.mutateAsync({
                      document: verified.document,
                      prefillId: verified.prefillId!,
                      email,
                    })
                  }
                  onConfirm={async (email, code) => {
                    const result = await emailVerify.mutateAsync({
                      document: verified.document,
                      prefillId: verified.prefillId!,
                      email,
                      code,
                    })
                    update('email', result.email)
                    setIdentity({ ...verified, emailGrantId: result.emailGrantId })
                    setChangingEmail(false)
                    return result
                  }}
                  onCancel={() => setChangingEmail(false)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setChangingEmail(true)}
                  className={publicTextAction}
                >
                  Trocar e-mail
                </button>
              )
            ) : undefined
          }
        />
      ) : null}
      {step === 'guardian' ? <GuardianStep {...props} minor={minor} /> : null}
      {step === 'review' ? (
        <ReviewStep
          {...props}
          clinicName={opening.clinic.name}
          privacyNotice={opening.privacyNotice}
          privacyAccepted={privacyAccepted}
          onPrivacy={(accepted) => {
            setPrivacyAccepted(accepted)
            touch('privacyAccepted')
          }}
          summary={summaryOf(values, today)}
          onEdit={goTo}
        />
      ) : null}

      {generalError ? (
        <ActionError
          className="mt-6"
          title={conflict ? 'Este CPF já tem cadastro' : 'Não foi possível enviar o cadastro'}
          description={generalError}
          actions={
            conflict ? (
              <Button type="button" variant="text" size="sm" onClick={() => goTo('you')}>
                Conferir o CPF
              </Button>
            ) : null
          }
        />
      ) : null}

      <div className="tablet:flex-row tablet:justify-end mt-8 flex flex-col-reverse gap-3">
        {index > 0 ? (
          <Button
            type="button"
            variant="outline"
            className="tablet:w-auto w-full"
            onClick={() => goTo(steps[index - 1]!)}
          >
            Voltar
          </Button>
        ) : null}
        {step === 'review' ? (
          <Button
            type="submit"
            className="tablet:w-auto w-full"
            loading={register.isPending}
            loadingLabel="Enviando…"
          >
            Enviar cadastro
          </Button>
        ) : awaitingCode && awaitingCode.options.emails.length > 0 ? null : (
          <Button
            type="submit"
            className="tablet:w-auto w-full"
            loading={identityLookup.isPending}
            loadingLabel="Conferindo o documento…"
          >
            Continuar
          </Button>
        )}
      </div>
    </PublicScreen>
  )
}
